import {useTranslation} from '@aboutyou/i18n';
import {useHistory} from '@aboutyou/router';
import {useTrackFeature} from '@aboutyou/tracking';
import {
  COLORS,
  GenericIconName,
  Hint,
  Icon,
  Text,
  UnstyledButton,
} from '@aboutyou/ui-components';
import React from 'react';
import styled, {css} from 'styled-components';
import {usePotentialFilters} from '../../ProductStream/utils';
import {PersonalizedFilter} from './utils/usePersonalizedFiltersStore';

export const PersonalizedFilterRow: React.FC<{
  dataTestId: string;
  personalizedFilter: PersonalizedFilter;
  showSuccessMessage: boolean;
  deletePersonalizedFilter: (personalizedFilter: PersonalizedFilter) => void;
  closeDropdown: VoidFunction;
  isFilterApplied: boolean;
}> = ({
  dataTestId,
  personalizedFilter,
  showSuccessMessage,
  deletePersonalizedFilter,
  closeDropdown,
  isFilterApplied,
}) => {
  const {t} = useTranslation();
  const trackFeature = useTrackFeature();
  const history = useHistory();
  const {resetAllFilters} = usePotentialFilters();

  const handleApplyFilterClick = () => {
    const {categoryName, filters, fullUrl} = personalizedFilter;

    closeDropdown();

    if (isFilterApplied) {
      resetAllFilters();
    } else {
      history.push(fullUrl);

      trackFeature({
        eventType: 'click',
        label: `persoFilter|applyFilter|${categoryName}|${filters.length}`,
      });
    }
  };

  return (
    <Wrapper
      data-testid={dataTestId}
      data-is-filter-applied={isFilterApplied}
      onClick={handleApplyFilterClick}
    >
      <Category size="px10" color="Grey1" weight="bold" transform="uppercase">
        {personalizedFilter.categoryName}
      </Category>

      <Row>
        <FilterList>
          {personalizedFilter.filters.length === 0 && (
            <Filter size="px10">{t('No Category Filters selected')}</Filter>
          )}
          {personalizedFilter.filters.map((filter, index) => (
            <Filter key={index} size="px10">
              {filter}
            </Filter>
          ))}
        </FilterList>

        <DeleteFilterIcon
          data-testid={`${dataTestId}DeleteButton`}
          onClick={e => {
            e.stopPropagation();
            deletePersonalizedFilter(personalizedFilter);
          }}
          asset={GenericIconName.Trash}
          size="S"
          fill={COLORS.Grey1}
        />

        <ToggleFilterButton aria-label={undefined}>
          <Icon
            asset={
              isFilterApplied
                ? GenericIconName.Checkmark
                : GenericIconName.ArrowLongRight
            }
            size="XS"
            fill={COLORS.White}
          />
        </ToggleFilterButton>
      </Row>

      {showSuccessMessage && (
        <SuccessMessage data-testid={`${dataTestId}SuccessMessage`}>
          <StyledHint
            label={t('Filter Set successfully saved')}
            fontSize="px12"
            weight="light"
            backgroundColor={COLORS.Green}
          >
            <Icon asset={GenericIconName.Checkmark} size="S" />
          </StyledHint>
        </SuccessMessage>
      )}
    </Wrapper>
  );
};

const Category = styled(Text)`
  margin: 0 16px 8px 16px;
`;

const ToggleFilterButton = styled(UnstyledButton)`
  display: flex;
  align-items: center;
  padding: 0 6px;
  margin-right: 16px;
  background-color: ${COLORS.Grey1};
  border-radius: 4px;
  height: 28px;
`;

const DeleteFilterIcon = styled(Icon)`
  margin: 3px 8px 0 1px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.3s ease;

  &:hover {
    fill: ${COLORS.Black};
  }
`;

const FilterList = styled.div`
  display: flex;
  width: 100%;
  gap: 6px;
  flex-wrap: wrap;
  position: relative;

  margin-left: 16px;
`;

const filterAppliedStyle = css`
  background-color: ${COLORS.Grey4};

  ${Category} {
    color: ${COLORS.Black};
  }

  ${ToggleFilterButton} {
    background-color: ${COLORS.Black};
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 0;
  border-bottom: 1px solid ${COLORS.Grey2};
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    ${filterAppliedStyle}

    ${DeleteFilterIcon} {
      opacity: 1;
    }
  }

  &[data-is-filter-applied='true'] {
    ${filterAppliedStyle}
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Filter = styled(Text)`
  padding: 7px 9px;
  background-color: ${COLORS.White};
  border-radius: 4px;
  border: 1px solid ${COLORS.Grey2};
  white-space: nowrap;
  cursor: pointer;
`;

const SuccessMessage = styled.div`
  margin: 12px 16px 0;
`;

const StyledHint = styled(Hint)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-direction: row-reverse;
  border-radius: 4px;
  padding: 3px 6px;
`;
